// --- PF Funding Module (Refactored) ---
// Files represented below (copy each into its own file if you want):
// - PfFunding.jsx            (default export)
// - FundingTable.jsx
// - ValuationPopup.jsx
// - FundingPopup.jsx
// - fundingUtils.js

// For convenience I've put them all in one JS module below. Split as needed.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Toast from "./Toast";

/* ------------------------- fundingUtils.js ------------------------- */
export const defaultStages = ["MVP", "GTM", "Expansion", "Scale", "IPO"];

export const getDefaultWeight = (activity) => {
  switch (activity) {
    case "Subscribers":
      return 500;
    case "IP":
      return 10000000;
    case "Deficit":
      return -1;
    case "Assets":
      return 1;
    case "Budget":
      return 1;
    default:
      return 1;
  }
};

export const getStageActivities = (stage) => {
  switch (stage) {
    case "MVP":
      return ["Budget", "Subscribers"];
    case "GTM":
      return ["Budget", "Subscribers", "Assets"];
    case "Expansion":
      return ["Budget", "Subscribers", "Assets", "IP"];
    case "Scale":
      return ["Budget", "Subscribers", "Assets", "IP", "Deficit"];
    default:
      return ["Budget", "Subscribers"];
  }
};

export const getPresetRows = (round) => {
  const base = [];
  const stage = round.stage || "MVP";
  const push = (activity, value) => base.push({ activity, value, weight: getDefaultWeight(activity) });

  if (round.budget) push("Budget", round.budget);
  push("Subscribers", "10000");

  if (["GTM", "Expansion", "Scale"].includes(stage)) push("Assets", "50000000");
  if (["Expansion", "Scale"].includes(stage)) push("IP", "1");
  if (stage === "Scale") push("Deficit", "1000000");

  return base;
};

export const getFundingPresetRows = (round) => {
  const valuation = Number(round.valuation) || 0;
  const stage = round.stage || "MVP";
  const base = [];
  const push = (activity, value, weight, type = "other") => base.push({ activity, value, weight, type });

  switch (stage) {
    case "MVP":
      push("Equity Ask (Angel)", valuation * 0.15, 15, "equity");
      push("SAFE Notes", valuation * 0.05, 5, "debt");
      push("Grants", valuation * 0.02, 2, "grant");
      break;
    case "GTM":
      push("Equity Ask (Series A)", valuation * 0.20, 20, "equity");
      push("Convertible Notes", valuation * 0.10, 10, "debt");
      push("Debt Ask (Marketing)", valuation * 0.05, 5, "debt");
      break;
    case "Expansion":
      push("Equity Ask (Series B)", valuation * 0.25, 25, "equity");
      push("Convertible Notes", valuation * 0.10, 10, "debt");
      push("Debt Ask (Infra)", valuation * 0.10, 10, "debt");
      break;
    case "Scale":
      push("Equity Ask (Series C+)", valuation * 0.30, 30, "equity");
      push("SAFE/Convertible", valuation * 0.10, 10, "debt");
      push("Debt Ask (Growth)", valuation * 0.15, 15, "debt");
      push("Deficit Financing", valuation * 0.05, 5, "debt");
      break;
    default:
      push("Equity Ask", valuation * 0.20, 20, "equity");
      push("SAFE/Convertible", valuation * 0.10, 10, "debt");
  }

  return base;
};

export const getDefaultValuation = (stage) => {
  switch (stage) {
    case "MVP":
      return 10000000;
    case "GTM":
      return 50000000;
    case "Expansion":
      return 200000000;
    case "IPO":
      return 1000000000;
    default:
      return 0;
  }
};

/* ------------------------- ValuationPopup.jsx ------------------------- */
function ModalShell({ title, children, onClose }) {
  return (
    <div className="w3-modal" style={{ display: "block" }}>
      <div className="w3-modal-content" style={{ maxWidth: 760 }}>
        <header className="w3-container w3-light-grey">
          <span style={{ float: "right", cursor: "pointer" }} onClick={onClose}>✖</span>
          <h3 style={{ margin: 0 }}>{title}</h3>
        </header>
        <div className="w3-container" style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

function ValuationPopup({ round, rows, setRows, onSave, onClose }) {
  if (!round) return null;

  return (
    <ModalShell title={`Valuation Breakdown — ${round.year} (${round.stage || "MVP"})`} onClose={onClose}>
      {rows.map((row, idx) => (
        <div key={idx} style={{ marginBottom: 12 }}>
          <div className="w3-row-padding">
            <div className="w3-half">
              <label>Activity</label>
              <input
                className="w3-input w3-border"
                value={row.activity}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, activity: e.target.value } : r)))}
                placeholder="Activity"
              />
            </div>
            <div className="w3-quarter">
              <label>Value (₹)</label>
              <input
                className="w3-input w3-border"
                type="number"
                value={row.value}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, value: e.target.value } : r)))}
                placeholder="Value"
              />
            </div>
            <div className="w3-quarter">
              <label>Weight</label>
              <input
                className="w3-input w3-border"
                type="number"
                value={row.weight}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, weight: e.target.value } : r)))}
                placeholder="Weight"
              />
            </div>
          </div>
          <div style={{ marginTop: 6 }}>
            <button className="w3-button w3-red" onClick={() => setRows((prev) => prev.filter((_, j) => j !== idx))}>Remove</button>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 8 }}>
        <button className="w3-button w3-light-grey" onClick={() => setRows((prev) => [...prev, { activity: "", value: "", weight: 1 }])}>+ Add Row</button>
        <button className="w3-button w3-light-grey" style={{ marginLeft: 8 }} onClick={() => setRows(getPresetRows(round))}>Reset to Preset</button>
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>
          Total Valuation: ₹{rows.reduce((sum, r) => sum + Number(r.value || 0) * Number(r.weight || 1), 0).toLocaleString()}
        </strong>
      </div>

      <div style={{ marginTop: 12 }}>
        <button className="w3-button w3-teal" onClick={() => onSave(rows)}>Save</button>
        <button className="w3-button w3-light-grey" style={{ marginLeft: 8 }} onClick={onClose}>Cancel</button>
      </div>
    </ModalShell>
  );
}

/* ------------------------- FundingPopup.jsx ------------------------- */
function FundingPopup({ round, rows, setRows, onSave, onClose }) {
  if (!round) return null;

  const totalAsk = rows.reduce((sum, r) => sum + Number(r.value || 0), 0);

  return (
    <ModalShell title={`Funding Breakdown — ${round.year} (${round.stage || "MVP"})`} onClose={onClose}>
      <p style={{ color: "#666", marginTop: 0 }}>
        This breakdown shows how the total funding ask is distributed across instruments relevant to the selected stage.
      </p>

      {rows.map((row, idx) => (
        <div key={idx} style={{ marginBottom: 12 }}>
          <div className="w3-row-padding">
            <div className="w3-half">
              <label>Instrument</label>
              <input
                className="w3-input w3-border"
                value={row.activity}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, activity: e.target.value } : r)))}
              />
            </div>
            <div className="w3-quarter">
              <label>Value (₹)</label>
              <input
                className="w3-input w3-border"
                type="number"
                value={row.value}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, value: e.target.value } : r)))}
              />
            </div>
            <div className="w3-quarter">
              <label>Weight %</label>
              <input
                className="w3-input w3-border"
                type="number"
                value={row.weight}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === idx ? { ...r, weight: e.target.value } : r)))}
              />
            </div>
          </div>
          <div style={{ marginTop: 6 }}>
            <button className="w3-button w3-red" onClick={() => setRows((prev) => prev.filter((_, j) => j !== idx))}>Remove</button>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 8 }}>
        <button className="w3-button w3-light-grey" onClick={() => setRows((prev) => [...prev, { activity: "", value: "", weight: 0 }])}>+ Add Row</button>
        <button className="w3-button w3-light-grey" style={{ marginLeft: 8 }} onClick={() => setRows(getFundingPresetRows(round))}>Reset to Stage Presets</button>
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>Total Funding Ask: ₹{totalAsk.toLocaleString()}</strong>
      </div>

      <div style={{ marginTop: 12 }}>
        <button className="w3-button w3-teal" onClick={() => onSave(rows)}>Save</button>
        <button className="w3-button w3-light-grey" style={{ marginLeft: 8 }} onClick={onClose}>Cancel</button>
      </div>
    </ModalShell>
  );
}

/* ------------------------- FundingTable.jsx ------------------------- */
function FundingTable({ rounds, onEditValuation, onEditFunding, onChange }) {
  return (
    <table className="w3-table-all" style={{ width: "100%" }}>
      <thead>
        <tr>
          <th>#</th>
          <th>Year</th>
          <th>Stage</th>
          <th className="w3-hide">Budget</th>
          <th>Valuation <br /> (Pre-Money)</th>
          <th>Funding <br /> (Ask)</th>
        </tr>
      </thead>
      <tbody>
        {rounds.map((round, i) => (
          <tr key={i}>
            <td>{i + 1}</td>
            <td>{round.year}</td>

            <td className="">
              <select className="w3-hide w3-select" value={round.stage || ""} onChange={(e) => onChange(i, "stage", e.target.value)}>
                <option value="">Select stage</option>
                {defaultStages.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {round.stage || ""}
            </td>

            <td className="w3-hide" >
              <input
                className="w3-input w3-border"
                value={round.budget || ""}
                onChange={(e) => onChange(i, "budget", e.target.value)}
              />
            </td>

            <td style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }} onClick={() => onEditValuation(i)}>
              ₹{round.valuation || ""}
            </td>

            <td style={{ cursor: "pointer", color: "green", textDecoration: "underline" }} onClick={() => onEditFunding(i)}>
              ₹{round.fundingRequired || ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ------------------------- PfFunding.jsx (main) ------------------------- */
export default function PfFunding({ onDirtyChange }) {
  const [fundingRounds, setFundingRounds] = useState([]);
  const [initialRounds, setInitialRounds] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveType, setSaveType] = useState("success");

  // Popup states
  const [valuationEditorIndex, setValuationEditorIndex] = useState(null);
  const [valuationRows, setValuationRows] = useState([]);
  const [fundingEditorIndex, setFundingEditorIndex] = useState(null);
  const [fundingRows, setFundingRows] = useState([]);

  const { id } = useParams();

  // Load roadmap / project data
  useEffect(() => {
    if (!id) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/projects`)
      .then((res) => res.json())
      .then((data) => {
        const project = (data.lists || []).find((p) => String(p.id) === String(id));
        if (!project) return;

        // Try to get stages/roadmap from project if available, otherwise use defaultStages.
        const roadmapStages = project.roadmapStages || project.stages || null;

        const rounds = [];
        for (let i = 1; i <= 5; i++) {
          const fy = project[`fy${i}`] || "";
          const budget = project[`budget${i}`] || "";
          const stageFromProject = project[`stage${i}`] || (roadmapStages && roadmapStages[i - 1]?.stage) || defaultStages[i - 1];
          const budgetNum = Number(budget) || 0;
          const valuation = budgetNum ? (budgetNum * 10).toFixed(2) : getDefaultValuation(stageFromProject).toFixed(2);
          const fundingRequired = budgetNum ? (budgetNum * 2).toFixed(2) : "";

          if (fy || budget) {
            rounds.push({
              year: fy,
              budget,
              stage: stageFromProject || "MVP",
              valuation,
              fundingRequired,
              investorType: project[`investorType${i}`] || "",
              fundingType: project[`fundingType${i}`] || "",
              equityPercent: project[`equityPercent${i}`] || "",
              notes: project[`fundingNotes${i}`] || "",
              valuationBreakdown: project[`valuationBreakdown${i}`] ? safeParse(project[`valuationBreakdown${i}`]) : [],
              fundingBreakdown: project[`fundingBreakdown${i}`] ? safeParse(project[`fundingBreakdown${i}`]) : [],
            });
          }
        }

        setFundingRounds(rounds);
        setInitialRounds(rounds);
      })
      .catch((err) => console.error(err));
  }, [id]);

  // safe parse helper
  const safeParse = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    try { return JSON.parse(v); } catch (e) { return []; }
  };

  useEffect(() => {
    setIsDirty(JSON.stringify(fundingRounds) !== JSON.stringify(initialRounds));
  }, [fundingRounds, initialRounds]);

  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleChange = (index, field, value) => {
    setFundingRounds((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [field]: value };
      if (field === "budget") {
        const budgetNum = Number(value) || 0;
        updated.valuation = budgetNum ? (budgetNum * 10).toFixed(2) : updated.valuation;
        updated.fundingRequired = budgetNum ? (budgetNum * 2).toFixed(2) : updated.fundingRequired;
      }
      return updated;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveMessage("");

    const fd = new FormData();
    fundingRounds.forEach((round, i) => {
      fd.append(`fundingYear${i + 1}`, round.year);
      fd.append(`fundingBudget${i + 1}`, round.budget);
      fd.append(`fundingStage${i + 1}`, round.stage);
      fd.append(`fundingValuation${i + 1}`, round.valuation);
      fd.append(`fundingRequired${i + 1}`, round.fundingRequired);
      fd.append(`fundingInvestorType${i + 1}`, round.investorType);
      fd.append(`fundingType${i + 1}`, round.fundingType);
      fd.append(`fundingEquity${i + 1}`, round.equityPercent);
      fd.append(`fundingNotes${i + 1}`, round.notes);
      fd.append(`fundingValuationBreakdown${i + 1}`, JSON.stringify(round.valuationBreakdown || []));
      fd.append(`fundingBreakdown${i + 1}`, JSON.stringify(round.fundingBreakdown || []));
    });
    if (id) fd.append("id", id);
    const endpoint = id ? "updateProject" : "addProject";

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${process.env.REACT_APP_ADMIN_TOKEN}` },
        body: fd,
      });

      const text = await res.text();
      const data = JSON.parse(text || "{}");

      if (data.success) {
        setInitialRounds(fundingRounds.map(r => ({ ...r })));
        setIsDirty(false);
        setSaveMessage("✅ Funding rounds saved!");
        setSaveType("success");
      } else {
        setSaveMessage("❌ Save failed");
        setSaveType("error");
      }
    } catch (err) {
      console.error("Network error:", err);
      setSaveMessage("❌ Network error");
      setSaveType("error");
    }
  };

  // Open valuation editor
  const openValuationEditor = (index) => {
    setValuationEditorIndex(index);
    const existing = fundingRounds[index]?.valuationBreakdown || [];
    setValuationRows(existing.length ? existing : getPresetRows(fundingRounds[index] || { stage: "MVP" }));
  };

  // Save valuation rows
  const saveValuationRows = (rows) => {
    setFundingRounds((prev) => prev.map((r, i) => i === valuationEditorIndex ? { ...r, valuationBreakdown: rows, valuation: rows.reduce((sum, rr) => sum + Number(rr.value || 0) * Number(rr.weight || 1), 0).toFixed(2) } : r));
    setValuationEditorIndex(null);
  };

  // Open funding editor
  const openFundingEditor = (index) => {
    setFundingEditorIndex(index);
    const existing = fundingRounds[index]?.fundingBreakdown || [];
    setFundingRows(existing.length ? existing : getFundingPresetRows(fundingRounds[index] || { stage: "MVP" }));
  };

  const saveFundingRows = (rows) => {
    const total = rows.reduce((sum, r) => sum + Number(r.value || 0), 0).toFixed(2);
    setFundingRounds((prev) => prev.map((r, i) => i === fundingEditorIndex ? { ...r, fundingBreakdown: rows, fundingRequired: total } : r));
    setFundingEditorIndex(null);
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FundingTable
          rounds={fundingRounds}
          onEditValuation={openValuationEditor}
          onEditFunding={openFundingEditor}
          onChange={handleChange}
        />

        <div style={{ marginTop: 12 }}>
          <button type="submit" className="w3-button w3-teal" disabled={!isDirty}>Save</button>
          <button type="button" className="w3-button w3-light-grey" onClick={() => setFundingRounds(initialRounds)} style={{ marginLeft: 8 }}>Cancel</button>
        </div>
      </form>

      {valuationEditorIndex !== null && (
        <ValuationPopup
          round={fundingRounds[valuationEditorIndex]}
          rows={valuationRows}
          setRows={setValuationRows}
          onSave={saveValuationRows}
          onClose={() => setValuationEditorIndex(null)}
        />
      )}

      {fundingEditorIndex !== null && (
        <FundingPopup
          round={fundingRounds[fundingEditorIndex]}
          rows={fundingRows}
          setRows={setFundingRows}
          onSave={saveFundingRows}
          onClose={() => setFundingEditorIndex(null)}
        />
      )}

      <Toast message={saveMessage} type={saveType} onClose={() => setSaveMessage("")} />
    </div>
  );
}

/* ------------------------- end file ------------------------- */
